import React, { useEffect, useState } from "react"
import Select, {
  components,
  ContainerProps,
  ControlProps,
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  InputProps,
  OptionProps,
  PlaceholderProps,
  Props,
  SingleValueProps,
} from "react-select"
import { SelectOption } from "@interfaces/misc"
import clsx from "clsx"
import { IoChevronDown } from "react-icons/io5"
import { useField } from "formik"

export interface FormikSelectProps extends Props {
  error?: string
  defaultOption?: SelectOption
  containerClassname?: string
  controlClassname?: string
  optionClassname?: string
  setField: (field: string, value: any, shouldValidate?: boolean) => void
  valueType?: "label" | "province"
  name: string
}

export const FormikSelect: React.FC<FormikSelectProps> = ({
  className,
  containerClassname,
  controlClassname,
  optionClassname,
  setField,
  valueType,
  name,
  error,
  defaultOption,
  value,
  ...props
}) => {
  const SelectContainer: React.FC<ContainerProps> = ({ children, ...props }) => {
    return (
      <components.SelectContainer {...props} className={clsx(className)}>
        {children}
      </components.SelectContainer>
    )
  }

  const Control: React.FC<ControlProps> = ({ children, ...props }) => {
    return (
      <components.Control {...props} className={controlClassname}>
        {children}
      </components.Control>
    )
  }

  const IndicatorSeparator = () => {
    return <></>
  }

  const DropdownIndicator: React.FC<DropdownIndicatorProps> = ({ ...props }) => {
    return (
      <components.DropdownIndicator {...props}>
        <IoChevronDown className="!text-secondary-black" />
      </components.DropdownIndicator>
    )
  }

  const Option: React.FC<OptionProps> = ({ children, ...props }) => {
    return (
      <components.Option {...props} className={optionClassname}>
        {children}
      </components.Option>
    )
  }

  const Input: React.FC<InputProps> = ({ children, ...props }) => {
    return <components.Input {...props}>{children}</components.Input>
  }

  const SingleValue: React.FC<SingleValueProps> = ({ children, ...props }) => {
    return <components.SingleValue {...props}>{children}</components.SingleValue>
  }

  const Placeholder: React.FC<PlaceholderProps> = ({ children, ...props }) => {
    return <components.Placeholder {...props}>{children}</components.Placeholder>
  }

  const [selectValue, setSelectValue] = useState<SelectOption>()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, meta] = useField(name)
  const errorText = meta.error

  useEffect(() => {
    if (defaultOption) {
      setSelectValue(defaultOption)
    }
  }, [defaultOption])

  const handleChange = (value: SelectOption | null) => {
    if (!value) {
      return
    }

    switch (true) {
      case valueType === "label":
        setField(`${name}`, value.label)
        setSelectValue(value)
        break
      case valueType === "province":
        setField("province_state", value.label)
        setField("city", "")
        setSelectValue(value)
        break
      case value.value === "":
        // @ts-ignore
        setSelectValue(null)
        break
      default:
        setField(`${name}`, value.value)
        setSelectValue(value)
        break
    }
  }

  return (
    <Select
      {...props}
      components={{
        SelectContainer,
        Control,
        IndicatorSeparator,
        DropdownIndicator,
        Option,
        Input,
        SingleValue,
        Placeholder,
      }}
      key={`my_unique_select_key__${JSON.stringify(selectValue)}`}
      // @ts-ignore
      onChange={(value1) => handleChange(value1)}
      value={value ?? selectValue}
      styles={{
        control: (props): CSSObjectWithLabel =>
          ({
            ...props,
            border: "1px solid #ececec",
            borderColor: "#231F20",
            boxShadow: "0 0 0 #ececec",
            borderRadius: "5px",
            minHeight: 0,
            padding: "10px 18px",
            "&:hover": {
              border: "1px solid #0dad8e",
              boxShadow: "0 0 0 #0dad8e",
            },
            "&:focus": {
              border: "1px solid #0dad8e",
              boxShadow: "0 0 0 #0dad8e",
            },
            "@media(max-width: 576px)": {
              fontSize: "12px",
            },
          } as CSSObjectWithLabel),
        input: (props): CSSObjectWithLabel =>
          ({
            ...props,
            margin: 0,
            paddingTop: 0,
            paddingBottom: 0,
          } as CSSObjectWithLabel),
        placeholder: (props): CSSObjectWithLabel =>
          ({
            ...props,
            color: "#979797",
            marginLeft: 0,
            marginRight: 0,
          } as CSSObjectWithLabel),
        dropdownIndicator: (props): CSSObjectWithLabel =>
          ({
            ...props,
            padding: 0,
          } as CSSObjectWithLabel),
        valueContainer: (props): CSSObjectWithLabel =>
          ({
            ...props,
            padding: "0 5px 0 0",
          } as CSSObjectWithLabel),
        option: (props, { isSelected }): CSSObjectWithLabel =>
          ({
            ...props,
            backgroundColor: isSelected ? "#0DAD8E" : "white",
            "&:active": {
              backgroundColor: "rgba(13, 173, 142, 0.5)",
            },
            "@media(max-width: 576px)": {
              fontSize: "12px",
            },
          } as CSSObjectWithLabel),
      }}
    />
  )
}
